import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogContentText,
  DialogActions,
  CircularProgress
} from "@mui/material";
import { motion } from "framer-motion";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { API } from "aws-amplify";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckoutForm from "./CheckoutForm";
import { stripePublishableKey } from "../stripeConfig";
import UserContext from "./UserContext";
import { buildApiHeaders } from "./apiHeaders";

const INK = "#0f1121";
const INK_SOFT = "rgba(15, 17, 33, 0.62)";
const INK_MUTED = "rgba(15, 17, 33, 0.55)";
const INK_FAINT = "rgba(15, 17, 33, 0.42)";
const INK_HAIRLINE = "rgba(15, 17, 33, 0.08)";
const INK_HAIRLINE_SOFT = "rgba(15, 17, 33, 0.05)";
const ACCENT = "#3854e8";
const ACCENT_SOFT = "rgba(56, 84, 232, 0.12)";
const SURFACE = "#ffffff";
const CANVAS = "#f6f6f4";

const SANS = "'sohne-var', 'Helvetica Neue', 'Arial', sans-serif";
const MONO =
  "'JetBrains Mono', 'Söhne Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

const primaryButtonSx = {
  fontFamily: SANS,
  fontSize: "0.95rem",
  fontWeight: 600,
  textTransform: "none",
  letterSpacing: "0.2px",
  px: 3,
  py: 1.3,
  borderRadius: "12px",
  color: "#fafaf7",
  backgroundColor: INK,
  boxShadow:
    "inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 8px 18px -10px rgba(15, 17, 33, 0.45)",
  transition:
    "transform 0.15s ease, box-shadow 0.2s ease, background-color 0.2s ease",
  "&:hover": {
    backgroundColor: "#1a1d36",
    transform: "translateY(-1px)"
  },
  "&:active": { transform: "translateY(0) scale(0.985)" },
  "&.Mui-disabled": {
    backgroundColor: "rgba(15, 17, 33, 0.18)",
    color: "rgba(255, 255, 255, 0.85)",
    boxShadow: "none"
  }
};

const ghostButtonSx = {
  fontFamily: SANS,
  fontSize: "0.94rem",
  fontWeight: 500,
  textTransform: "none",
  letterSpacing: "0.2px",
  px: 2.25,
  py: 1.25,
  borderRadius: "12px",
  color: INK,
  backgroundColor: SURFACE,
  border: `1px solid ${INK_HAIRLINE}`,
  transition: "all 0.15s ease",
  "&:hover": {
    borderColor: "rgba(15, 17, 33, 0.32)",
    backgroundColor: SURFACE,
    transform: "translateY(-1px)"
  }
};

const dialogPaperSx = {
  borderRadius: "20px",
  border: `1px solid ${INK_HAIRLINE}`,
  boxShadow:
    "inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 24px 60px -28px rgba(15, 17, 33, 0.22)",
  backgroundColor: SURFACE
};

const stripeAppearance = {
  theme: "stripe",
  variables: {
    fontFamily: SANS,
    fontWeightNormal: "500",
    borderRadius: "12px",
    colorPrimary: ACCENT,
    colorText: INK,
    colorTextSecondary: INK_SOFT,
    colorBackground: SURFACE,
    colorDanger: "#c25a1f",
    spacingUnit: "4px"
  },
  rules: {
    ".Input": {
      border: "1px solid rgba(15, 17, 33, 0.14)",
      boxShadow: "inset 0 1px 0 rgba(15, 17, 33, 0.02)",
      padding: "12px 14px"
    },
    ".Input:focus": {
      border: `1px solid ${ACCENT}`,
      boxShadow: `0 0 0 3px ${ACCENT_SOFT}`
    },
    ".Label": {
      color: INK_MUTED,
      fontSize: "13px",
      fontWeight: "500"
    }
  }
};

const BENEFITS = [
  {
    title: "Unlimited feedback loops",
    body: "No 10-a-day ceiling. Drill until the answers feel rehearsed.",
    emphasized: true
  },
  {
    title: "All four practice modes",
    body: "Job title, posting, resume × posting, your own list."
  },
  {
    title: "Save and revisit",
    body: "Pin tough questions. Come back when you're sharper."
  }
];

const SkeletonLine = ({ w = "100%", h = 12, mb = 1.25 }) => (
  <Box
    sx={{
      width: w,
      height: h,
      borderRadius: "6px",
      mb,
      background:
        "linear-gradient(90deg, rgba(15, 17, 33, 0.05) 0%, rgba(15, 17, 33, 0.11) 50%, rgba(15, 17, 33, 0.05) 100%)",
      backgroundSize: "200% 100%",
      animation: "skeletonShimmer 1.4s ease-in-out infinite",
      "@keyframes skeletonShimmer": {
        "0%": { backgroundPosition: "200% 0" },
        "100%": { backgroundPosition: "-200% 0" }
      }
    }}
  />
);

function ActivatePayments() {
  const [open, setOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [error, setError] = useState(false);
  const { user } = useContext(UserContext);
  const [stripePromise] = useState(() => loadStripe(stripePublishableKey));

  useEffect(() => {
    async function fetchClientSecret() {
      setLoading(true);
      const name = user?.attributes?.name;
      try {
        const headers = await buildApiHeaders();
        const response = await API.post(
          "chatEndpoints",
          "/chat/create_payment",
          { body: { name }, headers }
        );
        const {
          clientId: cId,
          clientSecret: cSecret,
          subscriptionId: sId
        } = await response;
        setClientId(cId);
        setClientSecret(cSecret);
        setSubscriptionId(sId);
      } catch (err) {
        // Surface as the existing "didn't go through" dialog after the user
        // tries to open checkout.
      } finally {
        setLoading(false);
      }
    }
    if (!clientSecret && !subscriptionId && user) fetchClientSecret();
  }, [clientSecret, subscriptionId, user]);

  const handleClickOpen = () => setOpen(true);
  const handleClose = success => {
    setOpen(false);
    if (success) setConfirmation(true);
    else setError(true);
  };

  const paymentElementOptions = clientSecret
    ? { clientSecret, appearance: stripeAppearance }
    : null;

  return (
    <Box
      sx={{
        position: "relative",
        backgroundColor: CANVAS,
        minHeight: "100vh",
        py: { xs: 6, md: 10 },
        px: { xs: 2, sm: 3 },
        textAlign: "left",
        overflow: "hidden"
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse 65% 50% at 78% -5%, rgba(228, 222, 207, 0.5), transparent 65%), radial-gradient(ellipse 50% 40% at 14% 6%, rgba(56, 84, 232, 0.07), transparent 72%)",
          maskImage:
            "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0) 65%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0) 65%)",
          pointerEvents: "none",
          zIndex: 0
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(15, 17, 33, 0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(15, 17, 33, 0.035) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at 50% 0%, rgba(0,0,0,0.4), transparent 78%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at 50% 0%, rgba(0,0,0,0.4), transparent 78%)",
          pointerEvents: "none",
          zIndex: 0
        }}
      />

      <Container
        maxWidth="lg"
        sx={{ position: "relative", zIndex: 1, textAlign: "left" }}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 110, damping: 22 }}
          sx={{ mb: { xs: 5, md: 8 }, maxWidth: "44ch" }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              mb: 2.5
            }}
          >
            <Box
              component={motion.span}
              animate={{ opacity: [0.55, 1, 0.55] }}
              transition={{
                duration: 2.4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: ACCENT
              }}
            />
            <Typography
              sx={{
                fontFamily: MONO,
                fontSize: "0.7rem",
                fontWeight: 500,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: INK_MUTED
              }}
            >
              Upgrade to Pro
            </Typography>
          </Box>
          <Typography
            component="h1"
            sx={{
              fontFamily: SANS,
              fontWeight: 600,
              fontSize: { xs: "2.2rem", sm: "2.8rem", md: "3.2rem" },
              lineHeight: 1.02,
              letterSpacing: "-0.03em",
              color: INK,
              m: 0,
              mb: 2,
              maxWidth: "16ch"
            }}
          >
            Lift the daily ceiling.
          </Typography>
          <Typography
            sx={{
              fontFamily: SANS,
              fontSize: { xs: "1rem", md: "1.1rem" },
              lineHeight: 1.55,
              color: INK_SOFT,
              m: 0
            }}
          >
            Unlimited feedback loops. Same loop, no daily wall — for the week
            before the actual interview.
          </Typography>
        </Box>

        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            type: "spring",
            stiffness: 110,
            damping: 22,
            delay: 0.05
          }}
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 7fr) minmax(0, 5fr)"
            },
            gap: { xs: 4, md: 6 },
            alignItems: "stretch"
          }}
        >
          <Box
            sx={{
              backgroundColor: SURFACE,
              border: `1px solid ${INK_HAIRLINE}`,
              borderRadius: "20px",
              p: { xs: 3, md: 4 },
              boxShadow:
                "inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 24px 60px -32px rgba(15, 17, 33, 0.18), 0 4px 10px -6px rgba(15, 17, 33, 0.06)"
            }}
          >
            <Typography
              sx={{
                fontFamily: MONO,
                fontSize: "0.66rem",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: INK_FAINT,
                mb: 2.5
              }}
            >
              What you get
            </Typography>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
            >
              {BENEFITS.map((b, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr",
                    gap: 2,
                    alignItems: "start",
                    pb: i < BENEFITS.length - 1 ? 2.5 : 0,
                    borderBottom:
                      i < BENEFITS.length - 1
                        ? `1px solid ${INK_HAIRLINE_SOFT}`
                        : "none"
                  }}
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      backgroundColor: b.emphasized ? ACCENT : ACCENT_SOFT,
                      color: b.emphasized ? "#fafaf7" : ACCENT,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      mt: 0.25
                    }}
                  >
                    <CheckRoundedIcon sx={{ fontSize: 14 }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: SANS,
                        fontSize: "1rem",
                        fontWeight: 600,
                        color: INK,
                        lineHeight: 1.35,
                        mb: 0.5,
                        letterSpacing: "-0.005em"
                      }}
                    >
                      {b.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: SANS,
                        fontSize: "0.92rem",
                        lineHeight: 1.5,
                        color: INK_SOFT
                      }}
                    >
                      {b.body}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              backgroundColor: SURFACE,
              border: `1px solid ${ACCENT}`,
              borderRadius: "20px",
              boxShadow:
                "inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 0 0 4px rgba(56, 84, 232, 0.08), 0 24px 60px -28px rgba(15, 17, 33, 0.22)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden"
            }}
          >
            <Box sx={{ p: { xs: 3, md: 4 } }}>
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: ACCENT,
                  mb: 2.5
                }}
              >
                Pro
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 0.5,
                  mb: 1.5
                }}
              >
                <Typography
                  sx={{
                    fontFamily: MONO,
                    fontSize: "0.9rem",
                    color: INK_FAINT,
                    lineHeight: 1
                  }}
                >
                  $
                </Typography>
                <Typography
                  sx={{
                    fontFamily: MONO,
                    fontSize: "3.6rem",
                    fontWeight: 500,
                    letterSpacing: "-0.04em",
                    color: INK,
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 0.95
                  }}
                >
                  9
                </Typography>
                <Typography
                  sx={{
                    fontFamily: MONO,
                    fontSize: "1.4rem",
                    fontWeight: 500,
                    letterSpacing: "-0.03em",
                    color: INK_FAINT,
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 1
                  }}
                >
                  .99
                </Typography>
                <Typography
                  sx={{
                    fontFamily: SANS,
                    fontSize: "0.92rem",
                    color: INK_FAINT,
                    ml: 0.75
                  }}
                >
                  / month
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: SANS,
                  fontSize: "0.95rem",
                  lineHeight: 1.5,
                  color: INK_SOFT,
                  mb: 3
                }}
              >
                Charged monthly. Cancel any time from your profile.
              </Typography>
              <Button
                onClick={handleClickOpen}
                disabled={loading || !clientSecret || open}
                disableRipple
                endIcon={
                  !loading && clientSecret ? (
                    <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
                  ) : undefined
                }
                sx={{ ...primaryButtonSx, width: "100%" }}
              >
                {loading ? (
                  <CircularProgress size={18} sx={{ color: "#fafaf7" }} />
                ) : (
                  "Continue to checkout"
                )}
              </Button>
            </Box>
            <Box
              sx={{
                px: { xs: 3, md: 4 },
                py: 2,
                borderTop: `1px solid ${INK_HAIRLINE_SOFT}`,
                backgroundColor: "rgba(15, 17, 33, 0.015)"
              }}
            >
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: "0.66rem",
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: INK_FAINT
                }}
              >
                Secure checkout · Stripe
              </Typography>
            </Box>
          </Box>
        </Box>

        {loading && (
          <Box
            sx={{
              mt: 4,
              maxWidth: 500,
              border: `1px solid ${INK_HAIRLINE}`,
              borderRadius: "16px",
              backgroundColor: SURFACE,
              p: 3
            }}
          >
            <Typography
              sx={{
                fontFamily: MONO,
                fontSize: "0.66rem",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: INK_FAINT,
                mb: 2
              }}
            >
              Preparing secure checkout…
            </Typography>
            <SkeletonLine w="92%" />
            <SkeletonLine w="78%" />
            <SkeletonLine w="64%" mb={0} />
          </Box>
        )}
      </Container>

      {clientSecret && (
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          PaperProps={{ sx: { ...dialogPaperSx, maxWidth: 480, width: "100%" } }}
        >
          <DialogTitle
            sx={{
              fontFamily: SANS,
              fontWeight: 600,
              fontSize: "1.2rem",
              color: INK,
              letterSpacing: "-0.01em",
              pb: 1
            }}
          >
            Complete your upgrade
          </DialogTitle>
          <DialogContent>
            <Typography
              sx={{
                fontFamily: SANS,
                fontSize: "0.92rem",
                color: INK_SOFT,
                lineHeight: 1.55,
                mb: 2.5
              }}
            >
              $9.99 per month, charged today and on the same day each month.
            </Typography>
            <Elements stripe={stripePromise} options={paymentElementOptions}>
              <CheckoutForm handleClose={handleClose} />
            </Elements>
          </DialogContent>
        </Dialog>
      )}

      <Dialog
        open={confirmation}
        onClose={() => setConfirmation(false)}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            fontFamily: SANS,
            fontWeight: 600,
            fontSize: "1.2rem",
            color: INK,
            letterSpacing: "-0.01em"
          }}
        >
          You're on Pro
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              fontFamily: SANS,
              fontSize: "0.95rem",
              color: INK_SOFT,
              lineHeight: 1.55
            }}
          >
            The daily ceiling is gone. Run as many feedback loops as you need.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setConfirmation(false)}
            disableRipple
            sx={primaryButtonSx}
          >
            Back to practice
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={error}
        onClose={() => setError(false)}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            fontFamily: SANS,
            fontWeight: 600,
            fontSize: "1.2rem",
            color: INK,
            letterSpacing: "-0.01em"
          }}
        >
          Payment didn't go through
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              fontFamily: SANS,
              fontSize: "0.95rem",
              color: INK_SOFT,
              lineHeight: 1.55
            }}
          >
            Double-check the card details and try again. Nothing was charged.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setError(false)}
            disableRipple
            sx={ghostButtonSx}
          >
            Close
          </Button>
          <Button
            onClick={() => {
              setError(false);
              setOpen(true);
            }}
            disableRipple
            sx={primaryButtonSx}
          >
            Try again
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ActivatePayments;
